import { Checkbox, Col, Form, FormInstance, Radio, Row, Select } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWatch } from 'antd/es/form/Form'
import { CompanyDashboard } from '../../../types/dashboard/company/types'
import { DeliverySubscription } from '../../../types/deliverySubscription/DeliverySubscription'
import { ReportDataType } from '../../../redux/context/reports/types'
import { useBackend } from '../../../services/backend'
import ScenarioSelect from '../../scenario/components/select/ScenarioSelect'
import DimensionTreeSelect from '../../dimension/components/DimensionTreeSelect'
import { useDeliverySubscriptionForm } from '../hooks/useDeliverySubscriptionForm'
import { DeliverySubscriptionFormData } from '../types'
import SharedSubscriptionFields from './SharedSubscriptionFields'

const { Option } = Select

const INITIATOR_OPTIONS = ['monthly', 'periodLock', 'manual'] as const

const INCOME_STATEMENT_OPTIONS = [
  { id: 1, title: 'incomeStatementSelection1' },
  { id: 2, title: 'incomeStatementSelection2' },
  { id: 3, title: 'incomeStatementSelection3' },
  { id: 4, title: 'incomeStatementSelection4' },
  { id: 5, title: 'incomeStatementSelection5' },
  { id: 6, title: 'incomeStatementSelection6' },
  { id: 7, title: 'incomeStatementSelection7' },
  { id: 8, title: 'incomeStatementSelection8' }
] as { id: number; title: string }[]

interface CompanyDashboardSubscriptionFormProps {
  form: FormInstance<DeliverySubscriptionFormData>
  deliverySubscription?: DeliverySubscription
}

const CompanyDashboardSubscriptionForm: React.FC<CompanyDashboardSubscriptionFormProps> = ({
  form,
  deliverySubscription
}) => {
  const { t } = useTranslation()
  const { selectableCompanies, selectableUsers, companyId } = useDeliverySubscriptionForm(form, deliverySubscription)

  const [companyDashboards, setCompanyDashboards] = useState<CompanyDashboard[]>([])
  const [forecastDisabled, setForecastDisabled] = useState(false)
  const companyDashboardRequest = useBackend('/api/companies/{companyId}/dashboards')
  const reportId = useWatch('reportId', form)

  useEffect(() => {
    if (companyId) {
      companyDashboardRequest
        .get({ urlParams: { companyId } })
        .then((response: CompanyDashboard[]) => setCompanyDashboards(response))
        .catch(() => setCompanyDashboards([]))

      if (deliverySubscription) {
        form.setFieldsValue({ params: { dimensionId: deliverySubscription.params?.dimensionId } })
      }
    }

    return () => setCompanyDashboards([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  const selectedDashboard = companyDashboards.find(d => d.id === reportId)

  const dataSettingChange = (e: any) => {
    if (e.target.value === 'last12months') {
      const datatypes = form.getFieldValue('params')?.dataTypes || []
      form.setFieldsValue({
        params: { dataTypes: datatypes.filter((f: ReportDataType) => f !== ReportDataType.forecast) }
      })
      setForecastDisabled(true)
    } else {
      setForecastDisabled(false)
    }
  }

  return (
    <Form
      onValuesChange={changedValue => {
        if (changedValue.companyId) {
          form.resetFields(['userId', 'reportId', 'params', 'recipients'])
        }
      }}
      form={form}
      layout="vertical"
      className="delivery-subscription-form"
    >
      <SharedSubscriptionFields
        form={form}
        initiatorOptions={INITIATOR_OPTIONS}
        selectableCompanies={selectableCompanies}
        selectableUsers={selectableUsers}
      />

      <Form.Item label="Dashboard" name="reportId" rules={[{ required: true, message: t('global:required-field') }]}>
        <Select placeholder={t('subscriptionsPage:choose-dashboard')} showSearch optionFilterProp="children">
          {companyDashboards.map(dashboard => (
            <Option key={dashboard.id} value={dashboard.id}>
              {dashboard.name || t('global:default')}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label={t('contextDrawer:budgeting-scenario')} name={['params', 'budgetingScenarioId']}>
        <ScenarioSelect companyId={companyId} />
      </Form.Item>

      <Form.Item label={t('contextDrawer:dimension')} name={['params', 'dimensionId']}>
        <DimensionTreeSelect companyId={companyId} />
      </Form.Item>

      <Form.Item
        name={['params', 'dataSetting']}
        rules={[{ required: true, message: t('global:required-field') }]}
        label={t('subscriptionsPage:dataSettingTitle')}
      >
        <Radio.Group onChange={dataSettingChange}>
          <Radio key="last12months" value="last12months">
            {t('subscriptionsPage:last12months')}
          </Radio>
          <Radio key="startOfFiscalyear" value="startOfFiscalyear">
            {t('subscriptionsPage:startOfFiscalyear')}
          </Radio>
        </Radio.Group>
      </Form.Item>

      {selectedDashboard?.items.some((item: any) => item.type === 'INCOME_STATEMENT') && (
        <>
          <Form.Item
            name={['params', 'incomeStatementRepresentation']}
            label={t('subscriptionsPage:income-statement-presentation')}
            rules={[{ required: true, message: t('global:required-field') }]}
          >
            <Select>
              {INCOME_STATEMENT_OPTIONS.map(val => (
                <Option className="type" key={val.id} value={val.id}>
                  {t(`subscriptionsPage:${val.title}`)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={['params', 'financialStatementLevel']}
            label={t('subscriptionsPage:incomeStatementAccuracy')}
            rules={[{ required: true, message: t('global:required-field') }]}
          >
            <Select>
              <Option key="statementRow" value="statementRow">
                {t('subscriptionsPage:byAccountGroup')}
              </Option>
              <Option key="account" value="account">
                {t('subscriptionsPage:byAccount')}
              </Option>
            </Select>
          </Form.Item>
        </>
      )}

      {selectedDashboard?.items.some((item: any) => item.type === 'BALANCE_SHEET') && (
        <Form.Item
          name={['params', 'balanceSheetLevel']}
          label={t('subscriptionsPage:balanceSheetAccuracy')}
          rules={[{ required: true, message: t('global:required-field') }]}
        >
          <Select>
            <Option key="statementRow" value="statementRow">
              {t('subscriptionsPage:byAccountGroup')}
            </Option>
            <Option key="account" value="account">
              {t('subscriptionsPage:byAccount')}
            </Option>
          </Select>
        </Form.Item>
      )}

      <Form.Item
        name={['params', 'dataTypes']}
        initialValue={deliverySubscription?.params?.dataTypes}
        rules={[{ required: true, message: t('global:required-field') }]}
      >
        <Checkbox.Group style={{ width: '100%' }}>
          <Row>
            {Object.entries(ReportDataType).map(([key, dataType]: any) => (
              <Col key={key} span={8}>
                <Checkbox
                  key={dataType}
                  value={dataType}
                  disabled={dataType === ReportDataType.forecast && forecastDisabled}
                >
                  {t(`contextDrawer:${dataType}`)}
                </Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Form.Item>
    </Form>
  )
}

export default CompanyDashboardSubscriptionForm
